import { gql, useLazyQuery, useMutation } from "@apollo/client";

import {
  CegidCustomer,
  CegidDocumentType,
  CegidErrorFragment,
  CegidInvoiceDraft,
  CegidInvoiceLineInput,
  CegidInvoicePreviewResult,
  CegidProduct,
} from "./types";

const cegidCustomerFields = gql`
  fragment CegidCustomerFields on CegidCustomer {
    taxRegistrationNumber
    businessName
    addressDetail
    postcode
    city
    country
  }
`;

export const cegidInvoicePreviewQuery = gql`
  ${cegidCustomerFields}
  query CegidInvoicePreview($orderId: ID!) {
    cegidInvoicePreview(orderId: $orderId) {
      documentType
      customerTaxRegistrationNumber
      customerBusinessName
      cegidCustomer {
        ...CegidCustomerFields
      }
      paymentMechanism
      isPaid
      orderTotal
      hasDraft
      problems
      lines {
        orderLineId
        isShipping
        itemCode
        itemDescription
        scope
        existsInCegid
        cegidSalesPrice
        quantity
        unitPrice
        lineTotal
        taxPercentage
        taxCode
        discountPercentage
      }
    }
  }
`;

interface CegidInvoicePreviewQueryData {
  cegidInvoicePreview: CegidInvoicePreviewResult | null;
}
interface OrderIdVars {
  orderId: string;
}

export const useCegidInvoicePreviewQuery = () =>
  useLazyQuery<CegidInvoicePreviewQueryData, OrderIdVars>(cegidInvoicePreviewQuery, {
    fetchPolicy: "network-only",
  });

const cegidDraftFields = gql`
  fragment CegidInvoiceDraftFields on CegidInvoiceDraft {
    documentId
    documentType
    date
    customerTaxRegistrationNumber
    customerBusinessName
    customerAddress
    paymentMechanism
    cegidTotal
    cegidReportedTotal
    orderTotal
    pdfUrl
    problems
    canFinalize
    lines {
      itemCode
      description
      quantity
      unitPrice
      taxPercentage
      lineTotal
      expectedQuantity
      expectedUnitPrice
      expectedTaxPercentage
      matches
    }
  }
`;

export const cegidInvoiceDraftQuery = gql`
  ${cegidDraftFields}
  query CegidInvoiceDraft($orderId: ID!) {
    cegidInvoiceDraft(orderId: $orderId) {
      ...CegidInvoiceDraftFields
    }
  }
`;

interface CegidInvoiceDraftQueryData {
  cegidInvoiceDraft: CegidInvoiceDraft | null;
}

export const useCegidInvoiceDraftQuery = () =>
  useLazyQuery<CegidInvoiceDraftQueryData, OrderIdVars>(cegidInvoiceDraftQuery, {
    fetchPolicy: "network-only",
  });

export const cegidCustomerByNifQuery = gql`
  ${cegidCustomerFields}
  query CegidCustomerByNif($taxRegistrationNumber: String!) {
    cegidCustomerByNif(taxRegistrationNumber: $taxRegistrationNumber) {
      ...CegidCustomerFields
    }
  }
`;

interface CegidCustomerByNifQueryData {
  cegidCustomerByNif: CegidCustomer | null;
}

export const useCegidCustomerByNifQuery = () =>
  useLazyQuery<CegidCustomerByNifQueryData, { taxRegistrationNumber: string }>(
    cegidCustomerByNifQuery,
    { fetchPolicy: "network-only" },
  );

export const cegidProductSearchQuery = gql`
  query CegidProductSearch($search: String!) {
    cegidProductSearch(search: $search) {
      itemCode
      itemDescription
      salesPrice
    }
  }
`;

interface CegidProductSearchQueryData {
  cegidProductSearch: CegidProduct[] | null;
}
interface CegidProductSearchQueryVars {
  search: string;
}

export const useCegidProductSearchQuery = () =>
  useLazyQuery<CegidProductSearchQueryData, CegidProductSearchQueryVars>(cegidProductSearchQuery, {
    fetchPolicy: "network-only",
  });

export const cegidInvoiceDraftCreateMutation = gql`
  ${cegidDraftFields}
  mutation CegidInvoiceDraftCreate(
    $orderId: ID!
    $lines: [CegidInvoiceLineInput!]!
    $documentType: CegidDocumentType!
    $paymentMechanism: String
    $customerTaxRegistrationNumber: String
    $customerBusinessName: String
  ) {
    cegidInvoiceDraftCreate(
      orderId: $orderId
      lines: $lines
      documentType: $documentType
      paymentMechanism: $paymentMechanism
      customerTaxRegistrationNumber: $customerTaxRegistrationNumber
      customerBusinessName: $customerBusinessName
    ) {
      draft {
        ...CegidInvoiceDraftFields
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

interface CegidInvoiceDraftCreateMutationData {
  cegidInvoiceDraftCreate: {
    draft: CegidInvoiceDraft | null;
    errors: CegidErrorFragment[];
  };
}
interface CegidInvoiceDraftCreateMutationVars {
  orderId: string;
  lines: CegidInvoiceLineInput[];
  documentType: CegidDocumentType;
  paymentMechanism?: string;
  customerTaxRegistrationNumber?: string;
  customerBusinessName?: string;
}

export const useCegidInvoiceDraftCreateMutation = () =>
  useMutation<CegidInvoiceDraftCreateMutationData, CegidInvoiceDraftCreateMutationVars>(
    cegidInvoiceDraftCreateMutation,
  );

export const cegidInvoiceDraftFinalizeMutation = gql`
  mutation CegidInvoiceDraftFinalize($orderId: ID!, $documentId: ID!) {
    cegidInvoiceDraftFinalize(orderId: $orderId, documentId: $documentId) {
      invoice {
        id
        number
        url
        createdAt
        status
      }
      order {
        id
        invoices {
          id
          number
          url
          createdAt
          status
        }
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

// Same fields as the order page's Invoice fragment, so the cached order stays
// complete after finalizing.
interface CegidFinalizedInvoice {
  id: string;
  number: string;
  url: string;
  createdAt: string;
  status: string;
}

interface CegidInvoiceDraftFinalizeMutationData {
  cegidInvoiceDraftFinalize: {
    invoice: CegidFinalizedInvoice | null;
    order: { id: string; invoices: CegidFinalizedInvoice[] } | null;
    errors: CegidErrorFragment[];
  };
}

export const useCegidInvoiceDraftFinalizeMutation = () =>
  useMutation<CegidInvoiceDraftFinalizeMutationData, { orderId: string; documentId: string }>(
    cegidInvoiceDraftFinalizeMutation,
  );

const cegidInvoiceDraftDiscardMutation = gql`
  mutation CegidInvoiceDraftDiscard($orderId: ID!) {
    cegidInvoiceDraftDiscard(orderId: $orderId) {
      errors {
        field
        code
        message
      }
    }
  }
`;

interface CegidInvoiceDraftDiscardMutationData {
  cegidInvoiceDraftDiscard: { errors: CegidErrorFragment[] };
}

export const useCegidInvoiceDraftDiscardMutation = () =>
  useMutation<CegidInvoiceDraftDiscardMutationData, OrderIdVars>(cegidInvoiceDraftDiscardMutation);
